import EyeOpenIcon from '@/assets/icons/EyeOpenIcon.svg?react';
import EyeOffIcon from '@/assets/icons/EyeOffIcon.svg?react';
import z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Input from '@/components/V3/Input';
import { Button } from '@/components/atoms/NewPcButton';
import { useEffect, useState } from 'react';
import { CPLAT_API_ROUTES, usePost } from '@/api';
import { ROUTES } from '@/routes';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/V3/useToast';
import GreenCheckIcon from '@/assets/icons/GreenCheckIcon.svg?react';
import { cn } from '@/utils/tailwind';
import { useAppSelector } from '@/redux/store';
import { useLogEvent } from '@/hooks/use-log-event';
import { GA_EVENT_NAMES, GA_ROUTES } from '@/constants/ga';

interface Props {
  verificationToken?: string;
  isMypage?: boolean;
  onCompleted?: () => void;
  onCanceled?: () => void;
}

const formSchema = z
  .object({
    password: z
      .string()
      .min(8, { message: '영문, 숫자, 특수문자를 포함한 8~20자리' })
      .max(20, { message: '영문, 숫자, 특수문자를 포함한 8~20자리' })
      .regex(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+{};:,<.>]).{8,20}$/, {
        message: '영문, 숫자, 특수문자를 포함한 8~20자리',
      }),
    confirm_password: z.string(),
  })
  .refine((values) => values.password && values.password === values.confirm_password, {
    message: '비밀번호가 일치하지 않습니다.',
    path: ['confirm_password'],
  });

type FormType = z.infer<typeof formSchema>;

export const ResetPasswordForm = ({
  verificationToken,
  isMypage = false,
  onCompleted,
  onCanceled,
}: Props) => {
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.user);

  const { showToast } = useToast();
  const { logEvent } = useLogEvent();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordVerified, setPasswordVerified] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    clearErrors,
  } = useForm<FormType>({
    resolver: zodResolver(formSchema),
  });
  const watchedPassword = watch('password');
  const watchedConfirmPassword = watch('confirm_password');

  const { mutate: onSigninByPhone } = usePost(
    CPLAT_API_ROUTES.signinByPhone,
    undefined,
    undefined,
    true,
  );

  const { mutate: onUpdatePassword } = usePost(
    CPLAT_API_ROUTES.updateAccount,
    undefined,
    undefined,
    true,
  );

  useEffect(() => {
    if (watchedPassword && watchedPassword === watchedConfirmPassword) {
      setPasswordVerified(true);
      clearErrors();
    } else setPasswordVerified(false);
  }, [watchedPassword, watchedConfirmPassword]);

  return (
    <div
      className={cn(
        'flex  h-full w-full flex-col justify-between max-md:justify-start max-md:gap-5',
        {
          'gap-5': isMypage,
        },
      )}
    >
      <div className={cn('space-y-5 max-md:space-y-3', { 'space-y-2.5': isMypage })}>
        <div className="space-y-2">
          {!isMypage && <div className="text-gr-800 Caption1M12">새 비밀번호</div>}
          <Input
            placeholder="영문,숫자,특수문자를 포함한 8~20자리"
            {...register('password')}
            Error={{
              isError: !!errors.password?.message,
              errorMessage: errors.password?.message,
            }}
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: watchedPassword && (
                <button onClick={() => setShowPassword((prev) => !prev)}>
                  {showPassword ? (
                    <EyeOpenIcon className="text-gr-400" />
                  ) : (
                    <EyeOffIcon className="text-gr-400" />
                  )}
                </button>
              ),
              endAdornmentStyle: 'top-6 right-2.5',
            }}
          />
        </div>
        <div className="space-y-2">
          {!isMypage && <div className="text-gr-800 Caption1M12">비밀번호 확인</div>}
          <Input
            placeholder="비밀번호를 한번 더 입력해 주세요"
            {...register('confirm_password')}
            Error={{
              isError: !!errors.confirm_password?.message,
              errorMessage: errors.confirm_password?.message,
            }}
            type={showConfirmPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: passwordVerified ? (
                <GreenCheckIcon />
              ) : (
                watchedConfirmPassword && (
                  <button onClick={() => setShowConfirmPassword((prev) => !prev)}>
                    {showConfirmPassword ? (
                      <EyeOpenIcon className="text-gr-400" />
                    ) : (
                      <EyeOffIcon className="text-gr-400" />
                    )}
                  </button>
                )
              ),
              endAdornmentStyle: 'top-6 right-2.5',
            }}
          />
        </div>
      </div>
      {isMypage ? (
        <div className="flex gap-3">
          <Button variant="secondary" className="flex-1" onClick={onCanceled}>
            취소
          </Button>
          <Button
            disabled={!passwordVerified}
            className="flex-1"
            onClick={handleSubmit((values) => {
              const cplatToken = user.CplatToken;
              onUpdatePassword(
                { cplatToken, plainPassword: values.password },
                {
                  onSuccess: () => {
                    if (onCompleted) onCompleted();
                    else {
                      showToast('success', '비밀번호가 재설정되었어요!');
                      navigate(ROUTES.root);
                    }
                  },
                },
              );
            })}
          >
            재설정
          </Button>
        </div>
      ) : (
        <Button
          disabled={!watchedPassword || !watchedConfirmPassword}
          onClick={handleSubmit((values) => {
            logEvent({
              eventName: GA_EVENT_NAMES.resetPassword.submit,
              pageTitle: GA_ROUTES.resetPassword,
            });

            onSigninByPhone(
              { verificationToken },
              {
                onSuccess: (response) => {
                  const cplatToken = response.data.data[0].cplatToken;

                  onUpdatePassword(
                    { cplatToken, plainPassword: values.password },
                    {
                      onSuccess: () => {
                        logEvent({
                          eventName: GA_EVENT_NAMES.resetPassword.success,
                          pageTitle: GA_ROUTES.resetPassword,
                        });
                        if (onCompleted) onCompleted();
                        else {
                          showToast('success', '비밀번호가 재설정되었어요!');
                          navigate(ROUTES.root);
                        }
                      },
                    },
                  );
                },
              },
            );
          })}
        >
          설정완료
        </Button>
      )}
    </div>
  );
};
